import React from 'react'
import { useNavigate } from 'react-router'
import { useRecoilState } from 'recoil'

import db from '../db/db'
import initFirebase from '../db/initFirebase'
import routes from '../pages/routes'
import { sessionState } from '../state'
import { clearAllCaches, getInitialSessionState } from '../utils/cacheHelpers'

/**
 * useAppBar - Custom hook for managing app bar state and actions
 *
 * Handles drawer state, user authentication, theme toggling,
 * and navigation actions for the application bar.
 */
export function useAppBar() {
  const [open, setOpen] = React.useState(false)
  const [session, setSession] = useRecoilState(sessionState)
  const navigate = useNavigate()

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const handleSignOut = async () => {
    try {
      const firestore = initFirebase.getFireBaseStore().firestore

      // Clear all caches (Firestore, browser storage, service worker)
      await clearAllCaches(firestore)

      // Sign out from Firebase Auth
      await initFirebase.getFireBaseStore().auth.signOut()

      // Reset Recoil state to initial values
      setSession(getInitialSessionState())

      // Navigate to home and replace history to prevent back navigation
      navigate(routes.home, { replace: true })

      // Force page reload to ensure complete cleanup
      window.location.reload()
    } catch (error) {
      console.error('Sign Out Error', error)
      // Fallback: still try to sign out and navigate even if cache clearing fails
      try {
        await initFirebase.getFireBaseStore().auth.signOut()
        setSession(getInitialSessionState())
        navigate(routes.home, { replace: true })
        window.location.reload()
      } catch (fallbackError) {
        console.error('Fallback sign out also failed:', fallbackError)
      }
    }
  }

  const toggleDarkMode = async () => {
    const user = await db.query({
      collection: 'users',
      where: ['uid', '==', session.user?.uid]
    })

    if (user.length > 0) {
      const currentTheme = user[0].theme ? user[0].theme : 'light'
      const updatedUser = { ...user[0] }
      updatedUser.theme = currentTheme === 'light' ? 'dark' : 'light'
      setSession(old => ({ ...old, user: updatedUser }))

      await db.updateItem('users', user[0].id ?? '', updatedUser)
    }
  }

  return {
    open,
    session,
    handleDrawerOpen,
    handleDrawerClose,
    handleSignOut,
    toggleDarkMode
  }
}
